/// <amd-module name="Feed/Legacy/Actions/FeedSettings/SaveFeedDetailsHandler" />
import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

import { Exceptions } from "Feed/Common/Constants/Constants";
import { Feed, FeedRetentionPolicy } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { IServerError } from "Feed/Common/Types/IError";
import { IFeedSettingsState, IRetentionPolicySettingsState } from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import { IFeedDataService } from "Feed/Legacy/DataServices/FeedsDataService";
import { Feed as Feed_ } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

/**
 * The user clicked save in feed details pivot
 */
export class SaveFeedDetailsHandler {
    public static async handleAsync(state: IFeedSettingsState): Promise<Feed> {
        let updatedFeed: Feed_;

        try {
            updatedFeed = (await this.setFeedDetailsAsync(state)) as Feed_;
            announce(PackageResources.SavedAnnouncement);
            // if the description is empty the server will return null, set it to empty to update the UI
            if (state.feedDescription === "") {
                updatedFeed.description = "";
            }

            state.feedName = null;
            state.feedDescription = null;
            state.hideDeletedPackageVersions = null;
            state.badgesEnabled = null;
        } catch (reason) {
            const errMessage: string = this.getErrorMessage(reason, state);
            reason.message = errMessage;

            announce(PackageResources.SavedAnnouncement_Failed);

            throw reason;
        }

        const retentionPolicySettings = state.retentionPolicySettings;

        const feed = state.feed();
        const projectId = feed.project ? feed.project.id : null;
        retentionPolicySettings.retentionPolicy = await this.setFeedRetentionPolicyAsync(
            state.pageContext,
            retentionPolicySettings,
            feed.id,
            projectId
        );
        retentionPolicySettings.retentionPolicyLoading = false;
        retentionPolicySettings.retentionPolicyToApply = null;

        return updatedFeed;
    }

    private static getErrorMessage(reason: IServerError, state: IFeedSettingsState): string {
        if (reason.serverError != null && reason.serverError.typeKey === Exceptions.FeedNameAlreadyExistsException) {
            return format(PackageResources.FeedSettings_FeedRenameError_AlreadyExists, state.feedName);
        }
        if (reason.serverError != null && reason.serverError.typeKey === Exceptions.FeedNotReleasedException) {
            return format(PackageResources.FeedSettings_FeedRenameError_NotYetReleased, state.feedName);
        }
        return format(PackageResources.FeedSettings_FeedRenameError, reason.message);
    }

    private static async setFeedDetailsAsync(state: IFeedSettingsState): Promise<Feed> {
        const feed = state.feed();

        const feedToUpdate: Feed = {
            id: feed.id,
            name: state.feedName,
            description: state.feedDescription,
            hideDeletedPackageVersions: state.hideDeletedPackageVersions,
            badgesEnabled: state.badgesEnabled,
            project: feed.project
        } as Feed;

        // TODO: in a case where you only update retention policies, this API call is unnecessary.
        // Add logic to check if that's the case and just return state.feed().
        const feedDataService = state.pageContext.getService<IFeedDataService>("IFeedDataService");
        return feedDataService.updateFeedAsync(feedToUpdate);
    }

    private static async setFeedRetentionPolicyAsync(
        pageContext: IVssPageContext,
        state: IRetentionPolicySettingsState,
        feedId: string,
        projectId: string
    ): Promise<FeedRetentionPolicy> {
        const newPolicy = state.retentionPolicyToApply;
        if (newPolicy) {
            const feedDataService = pageContext.getService<IFeedDataService>("IFeedDataService");

            if ((newPolicy.ageLimitInDays == null || newPolicy.ageLimitInDays === 0) && newPolicy.countLimit == null) {
                return feedDataService.deleteFeedRetentionPolicy(projectId, feedId);
            } else {
                return feedDataService.setFeedRetentionPolicyAsync(projectId, feedId, newPolicy);
            }
        } else {
            return Promise.resolve(state.retentionPolicy);
        }
    }
}
